import React from "react";
import {parseBookingTimestamp} from "../../../util/DateUtils";
import {
  BookingStatus,
  BookingStatusesEligibleForMakingPayment,
  BookingStatusesEligibleForPaymentMethodUpdate
} from '../../constants/securspace-constants';
import {formatBookingLocation, RECURRING, startDateLessThan24Hours} from "../../../util/BookingUtil";
import moment from "moment";
import {atcb_action} from "add-to-calendar-button-react";
import {constructQrCode} from "../../../util/ConstructImageUrlUtil";
import BookingCard from "../../BookingCard/BookingCard";
import {showEndBookingStatuses} from "./data";

const CANCELLED = 'CANCELLED';

const BuyerBookingCardItem = ({
                                booking,
                                headerCells,
                                onEnd,
                                onUpdate,
                                onShowPaymentModal,
                                onShowCancelModal,
                                onMakePayment
                              }) => {
  const {
    id,
    status,
    bookingSubscriptionStatus,
    frequency,
    failedOveragesCount,
    undisputedFailedPaymentsCount
  } = booking;

  // Gate hours / after-hours access only apply when the location is NOT 24/7;
  // hide them for 24/7 locations even if values are present.
  const accessOnlyFieldIds = ['locationGateHours', 'locationAfterHoursAccessProcedure'];

  // Filter out header cells that are null or undefined or an empty string in the booking data
  const filteredHeaderCells = headerCells.filter(cell =>
    (cell.id === "creationDate" || (Object.prototype.hasOwnProperty.call(booking, cell.id) &&
    booking[cell.id] != null && String(booking[cell.id]).trim() !== '')) &&
    !(booking.locationHas247Access === true && accessOnlyFieldIds.includes(cell.id))
  );

  const declinedHeaderCells = [
    {id: 'reasonDeclined', label: 'REASON DECLINED'},
    {id: 'reasonDeclinedNote', label: 'REASON DECLINED NOTE'}
  ];

  const updatedHeaderCells = status === BookingStatus.DECLINED
    ? [...filteredHeaderCells, ...declinedHeaderCells]
    : filteredHeaderCells;

  const oneTimeBookingStartDateMoreThan24Hrs = frequency === RECURRING || !startDateLessThan24Hours(booking);

  const buttons = [];

  const shouldShowCancelOption = status === BookingStatus.PENDING || (status === BookingStatus.APPROVED && !startDateLessThan24Hours(booking));
  // An expired booking surfaces on the Inactive tab, but its bookingSubscriptionStatus can still read
  // ACTIVE because no batch job transitions recurring subscriptions when they lapse. Rely on the
  // backend-computed `active` flag (timezone-aware, already accounts for end date + status) so we do
  // not offer "End Booking" on a booking that has already ended.
  const shouldShowEndOption = booking.active !== false && bookingSubscriptionStatus !== CANCELLED && showEndBookingStatuses.includes(status) && oneTimeBookingStartDateMoreThan24Hrs && !shouldShowCancelOption;

  // A pre-start ACH monthly booking is a full cancellation (with refund), so label it "Cancel Booking"
  // and let handleEndBooking route it to the cancel+refund flow. Every other recurring booking keeps
  // the "End Booking" label/behavior.
  const isPreStartAchRecurring = frequency === RECURRING
    && status === BookingStatus.PROCESSING_ACH_PAYMENT
    && !startDateLessThan24Hours(booking);

  if (shouldShowEndOption) {
    buttons.push(
      {
        label: isPreStartAchRecurring || frequency !== RECURRING ? "Cancel Booking" : "End Booking",
        onClick: onEnd,
        variant: 'text',
        styles: {
          color: (theme) => theme.palette.error.main,
        }
      }
    );
  }

  if (shouldShowCancelOption) {
    buttons.push({
      label: 'Cancel Booking Request',
      onClick: onShowCancelModal,
      variant: 'text',
      styles: {
        color: (theme) => theme.palette.error.main,
      }
    });
  }

  if (BookingStatusesEligibleForPaymentMethodUpdate.includes(status) && failedOveragesCount === 0) {
    buttons.push(
      {
        label: 'Update Payment Method',
        onClick: onUpdate,
        variant: 'outlined'
      }
    );
  }
  if (BookingStatusesEligibleForMakingPayment.includes(status) || failedOveragesCount > 0) {
    const disabled = undisputedFailedPaymentsCount === 0 && failedOveragesCount === 0;
    buttons.push(
      {
        label: 'Make Payment',
        onClick: onMakePayment,
        variant: 'outlined',
        disabled: disabled,
        tooltip: disabled ? 'There are no outstanding payments for this booking.' : undefined
      }
    );
  }
  if (status === BookingStatus.INCOMPLETE) {
    buttons.push(
      {
        label: 'Complete Booking',
        onClick: onShowPaymentModal,
        variant: 'outlined'
      }
    );
  }
  if (booking.active) {
    buttons.push({
      label: 'Add to Calendar',
      onClick: (data) => {
        let description = data?.locationInstructions || "Provide your booking number when you arrive at the gate.";
        const creationDate = parseBookingTimestamp(data.creationDate);
        if (data.locationQrScannerAvailable && creationDate) {
          const qrCodeFilename = `${data.orderNumber}_${creationDate.getTime()}.png`;
          if (description.length > 0) {
            description += '[br]';
          }
          description += `[url]${constructQrCode(qrCodeFilename)}|View QR Code here.[/url]`;
        }
        const config = {
          name: `SecurSpace Booking # ${data.orderNumber} at ${data.locationName}`,
          startDate: moment(data.startDate).toISOString(),
          endDate: moment(data.endDate).toISOString(),
          location: formatBookingLocation(data),
          options: ['Apple', 'Google', 'Microsoft365', 'iCal'],
          listStyle: 'dropdown',
          description,
          hideBranding: true,
          iCalFileName: `securspace_booking_${data.orderNumber}_${moment(data.startDate).format('YYYYMMDD')}`,
        };
        atcb_action(config);
      },
      variant: 'outlined'
    });
  }

  return (
    <BookingCard
      bookingTitle={booking.bookingTitle}
      headCells={updatedHeaderCells}
      key={id}
      data={booking}
      buttons={buttons}
    />
  );
};

export default BuyerBookingCardItem;
